import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';

import './index.scss';

const Contact = () => {
  const position = [30.052044, 31.518578];
  const contactRootRef = useRef(null);
  const formFallbackTimer = useRef(null);
  const successTimer = useRef(null);

  const [isFormReady, setIsFormReady] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const handleFormMessage = (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data?.type === 'bigbee-zoho-form-ready') {
        window.clearTimeout(formFallbackTimer.current);
        setIsFormReady(true);
      }

      if (event.data?.type === 'bigbee-zoho-success') {
        const message =
          event.data.message ||
          'Thank you for contacting BigBee. We have received your message and will get back to you shortly.';

        window.clearTimeout(successTimer.current);
        setSuccessMessage(message);

        successTimer.current = window.setTimeout(() => {
          setSuccessMessage('');
        }, 5500);
      }
    };

    window.addEventListener('message', handleFormMessage);

    return () => {
      window.removeEventListener('message', handleFormMessage);
      window.clearTimeout(formFallbackTimer.current);
      window.clearTimeout(successTimer.current);
    };
  }, []);

  useEffect(() => {
    const root = contactRootRef.current;

    if (!root) {
      return undefined;
    }

    const revealElements = root.querySelectorAll('[data-contact-reveal]');

    if (!('IntersectionObserver' in window)) {
      revealElements.forEach((element) => {
        element.classList.add('is-visible');
      });

      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          entry.target.classList.add('is-visible');
          currentObserver.unobserve(entry.target);
        });
      },
      {
        threshold: 0.14,
        rootMargin: '0px 0px -45px 0px'
      }
    );

    revealElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [successMessage]);

  const handleIframeLoad = () => {
    /*
     * zoho-form.html confirms when its final styles are ready.
     * This timer is a fallback so the form can never remain hidden.
     */
    window.clearTimeout(formFallbackTimer.current);

    formFallbackTimer.current = window.setTimeout(() => {
      setIsFormReady(true);
    }, 2500);
  };

  const closeSuccessMessage = () => {
    window.clearTimeout(successTimer.current);
    setSuccessMessage('');
  };

  const successModal =
    successMessage && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="bigbee-success-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bigbee-success-title"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeSuccessMessage();
              }
            }}
          >
            <div className="bigbee-success-modal__card">
              <button
                type="button"
                className="bigbee-success-modal__close"
                aria-label="Close success message"
                onClick={closeSuccessMessage}
              >
                ×
              </button>

              <div className="bigbee-success-modal__icon" aria-hidden="true">
                <span />
              </div>

              <p className="bigbee-success-modal__eyebrow">Message received</p>

              <h2 id="bigbee-success-title">Thank you for contacting BigBee.</h2>

              <p className="bigbee-success-modal__message">{successMessage}</p>

              <button
                type="button"
                className="bigbee-success-modal__button"
                onClick={closeSuccessMessage}
              >
                Done
              </button>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <main className="premium-contact" id="contact" ref={contactRootRef}>
        <section className="premium-contact__hero">
          <div className="premium-contact__glow premium-contact__glow--one" />
          <div className="premium-contact__glow premium-contact__glow--two" />

          <div className="premium-contact__container premium-contact__grid">
            <div
              className="premium-contact__intro"
              data-contact-reveal
              style={{ '--reveal-delay': '40ms' }}
            >
              <p className="premium-contact__eyebrow">Let&apos;s work together</p>

              <h1 className="premium-contact__title">
                Build something
                <span>bold.</span>
              </h1>

              <p className="premium-contact__description">
                Tell us about your business, goals, and timeline. Your inquiry goes directly to our
                CRM, and our team will contact you with the right next step.
              </p>

              <div className="premium-contact__facts">
                <article className="premium-contact__fact">
                  <span className="premium-contact__fact-number">24H</span>
                  <span className="premium-contact__fact-label">Response time</span>
                </article>

                <article className="premium-contact__fact">
                  <span className="premium-contact__fact-number">MENA</span>
                  <span className="premium-contact__fact-label">Regional experience</span>
                </article>

                <article className="premium-contact__fact">
                  <span className="premium-contact__fact-number">CRM</span>
                  <span className="premium-contact__fact-label">Secure inquiry</span>
                </article>
              </div>

              <div className="premium-contact__quick-links">
                <a href="mailto:info@bigbee-marketing.com">
                  <span aria-hidden="true">@</span>
                  info@bigbee-marketing.com
                </a>

                <a href="tel:+201553689004">
                  <span aria-hidden="true">↗</span>
                  +20 155 368 9004
                </a>
              </div>
            </div>

            <div
              className="premium-contact__form-card"
              data-contact-reveal
              style={{ '--reveal-delay': '150ms' }}
            >
              <div className="premium-contact__form-heading">
                <div>
                  <p>Project inquiry</p>
                  <h2>Start your project</h2>
                </div>

                <span>
                  <strong>*</strong> Required fields
                </span>
              </div>

              <div
                className={`premium-contact__form-shell${isFormReady ? ' is-ready' : ''}`}
                aria-busy={!isFormReady}
              >
                <div className="premium-contact__form-loader" aria-hidden="true">
                  <span className="premium-contact__form-loader-mark" />
                  <p>Loading secure form</p>
                </div>

                <iframe
                  src={`${process.env.PUBLIC_URL}/zoho-form.html?v=20260727-4`}
                  title="BigBee project inquiry form"
                  className="premium-contact__form-frame"
                  scrolling="no"
                  loading="eager"
                  onLoad={handleIframeLoad}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="premium-contact__location">
          <div className="premium-contact__container">
            <div className="premium-contact__location-heading" data-contact-reveal>
              <div>
                <p className="premium-contact__eyebrow">Find us</p>
                <h2>Based in New Cairo.</h2>
              </div>

              <a
                href="https://maps.google.com/?q=30.052044,31.518578"
                target="_blank"
                rel="noreferrer"
                className="premium-contact__directions"
              >
                Get directions
                <span aria-hidden="true">↗</span>
              </a>
            </div>

            <div className="premium-contact__location-grid">
              <div
                className="premium-contact__map"
                data-contact-reveal
                style={{ '--reveal-delay': '80ms' }}
              >
                <MapContainer center={position} zoom={14} scrollWheelZoom={false}>
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  <Marker position={position}>
                    <Popup>
                      <a
                        href="https://maps.google.com/?q=30.052044,31.518578"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Navigate to BigBee
                      </a>
                    </Popup>
                  </Marker>
                </MapContainer>
              </div>

              <aside className="premium-contact__details">
                <article data-contact-reveal style={{ '--reveal-delay': '100ms' }}>
                  <span>01</span>
                  <div>
                    <small>Location</small>
                    <p>New Cairo, Cairo, Egypt</p>
                  </div>
                </article>

                <article data-contact-reveal style={{ '--reveal-delay': '160ms' }}>
                  <span>02</span>
                  <div>
                    <small>Phone</small>
                    <a href="tel:+201553689004">+20 155 368 9004</a>
                  </div>
                </article>

                <article data-contact-reveal style={{ '--reveal-delay': '220ms' }}>
                  <span>03</span>
                  <div>
                    <small>Email</small>
                    <a href="mailto:info@bigbee-marketing.com">info@bigbee-marketing.com</a>
                  </div>
                </article>

                <article data-contact-reveal style={{ '--reveal-delay': '280ms' }}>
                  <span>04</span>
                  <div>
                    <small>Coordinates</small>
                    <p>30.052044, 31.518578</p>
                  </div>
                </article>
              </aside>
            </div>
          </div>
        </section>
      </main>

      {successModal}
    </>
  );
};

export default Contact;
